// Mobile parity: pick → uploading (30s timer) → done / error.
// POST to {API_BASE}/users/resume multipart, parse resumeData, stash parsed
// fields (email/name/currRole/currCompany/phone/countryCode/linkedinUrl) into
// the OnboardingProvider.
import { useEffect, useRef, useState } from "react"
import { auth } from "../../firebaseConfig"
import { useOnboarding } from "../../contexts/OnboardingContext"
import {
  OnboardingStepShell,
  ContinueButton,
  OB_COLORS as C,
  OB_FF as FF,
} from "./OnboardingStepShell"

const API_BASE = "https://jobscanner-e1dd4.ue.r.appspot.com"
const MAX_BYTES = 10 * 1024 * 1024

function parseResume(data) {
  const pi = (data?.personal_information && typeof data.personal_information === "object") ? data.personal_information : {}
  const sl = (data?.social_links && typeof data.social_links === "object") ? data.social_links : {}
  const trim = (v) => (typeof v === "string" ? v.trim() : "")
  return {
    jobTitle:      trim(pi.currentJobTitle),
    company:       trim(pi.currentCompany),
    candidateName: trim(pi.name) || trim(pi.fullName) || trim(data?.name),
    email:         trim(data?.email),
    phone:         trim(data?.phone),
    countryCode:   trim(data?.countryCode),
    linkedinUrl:   trim(sl.linkedin),
  }
}

export default function ResumeUploadStep({ onContinue, onBack }) {
  const { updateField } = useOnboarding()
  const [phase, setPhase]       = useState("pick") // pick | uploading | done | error
  const [fileName, setFileName] = useState("")
  const [errorMsg, setErrorMsg] = useState("")
  const [referral, setReferral] = useState("")
  const inputRef = useRef(null)

  const onPickClick = () => inputRef.current?.click()

  const onFile = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return
    if (file.size > MAX_BYTES) {
      setErrorMsg("File must be under 10 MB")
      setPhase("error")
      return
    }
    setFileName(file.name)
    setPhase("uploading")
    setErrorMsg("")
    await runFlow(file)
  }

  const runFlow = async (file) => {
    try {
      const uid = auth.currentUser?.uid || "anonymous"
      const resumeName = `resume_${Date.now()}`
      const form = new FormData()
      form.append("userId", uid)
      form.append("name", resumeName)
      form.append("resumeFile", file, file.name)

      const ctrl = new AbortController()
      const timeout = setTimeout(() => ctrl.abort(), 90_000)

      let res, json
      try {
        res = await fetch(`${API_BASE}/users/resume`, {
          method: "POST",
          body: form,
          signal: ctrl.signal,
        })
      } catch (err) {
        clearTimeout(timeout)
        if (err?.name === "AbortError") {
          setErrorMsg("Request timed out. Check your connection.")
        } else {
          setErrorMsg("Upload failed. Please try again.")
        }
        setPhase("error")
        return
      }
      clearTimeout(timeout)

      try { json = await res.json() } catch { json = null }

      if (!res.ok || !json?.success) {
        setErrorMsg(json?.error || `Upload failed (${res.status})`)
        setPhase("error")
        return
      }

      const parsed = parseResume(json.resumeData || {})
      const authEmail = auth.currentUser?.email || null
      const isAppleRelay = typeof authEmail === "string" &&
        authEmail.toLowerCase().endsWith("@privaterelay.appleid.com")

      updateField({
        email:        isAppleRelay && parsed.email ? parsed.email : authEmail,
        name:         parsed.candidateName || undefined,
        currRole:     parsed.jobTitle || undefined,
        currCompany:  parsed.company || undefined,
        phone:        parsed.phone || undefined,
        countryCode:  parsed.countryCode || undefined,
        linkedinUrl:  parsed.linkedinUrl || undefined,
      })

      setPhase("done")
    } catch {
      setErrorMsg("Upload failed. Please try again.")
      setPhase("error")
    }
  }

  const retry = () => {
    setPhase("pick")
    setErrorMsg("")
    setFileName("")
  }

  return (
    <OnboardingStepShell
      title={"Let's build\nyour profile"}
      subtitle={"We'll match jobs and tailor every application from your résumé."}
      step={0}
      totalSteps={5}
      showProgress={false}
      onBack={onBack}
      bottom={phase === "done" ? <ContinueButton label="Continue" onClick={onContinue} /> : null}
    >
      <style>{`
        @keyframes rus-spin { to { transform: rotate(360deg); } }
        @keyframes rus-fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .rus-fade { animation: rus-fade 320ms ease forwards; }
      `}</style>

      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        onChange={onFile}
        style={{ display: "none" }}
      />

      {phase === "pick" && (
        <PickCard key="pick" onClick={onPickClick} />
      )}
      {phase === "uploading" && (
        <UploadingCard key="uploading" fileName={fileName} />
      )}
      {phase === "done" && (
        <DoneCard key="done" fileName={fileName} />
      )}
      {phase === "error" && (
        <ErrorCard key="error" message={errorMsg || "Upload failed. Please try again."} onRetry={retry} />
      )}

      {/* Referral code (optional, not wired) */}
      <div style={{ marginTop: 14 }}>
        <ReferralField value={referral} onChange={setReferral} />
      </div>

      <div style={{
        marginTop: 14,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        fontSize: 12, color: C.textFaint, fontFamily: FF,
      }}>
        <svg width="13" height="13" viewBox="0 0 20 20" fill="none">
          <path d="M10 2.5l6 2v4c0 4-2.7 7.3-6 9-3.3-1.7-6-5-6-9v-4l6-2z" stroke={C.textFaint} strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
        Private — we never post anything
      </div>
    </OnboardingStepShell>
  )
}

function ReferralField({ value, onChange }) {
  const [focused, setFocused] = useState(false)
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 14px",
      background: C.card,
      border: `1px dashed ${focused ? C.text : C.border}`,
      borderRadius: 14,
      transition: "border-color 0.15s",
      fontFamily: FF,
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: 7,
        background: "#FFF5EC", border: "1px solid #F7D9BE",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, color: "#F25A20",
      }}>
        <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
          <path d="M4 7l6-4 6 4v6l-6 4-6-4V7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M10 3v14M4 7l6 4 6-4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, letterSpacing: "0.03em" }}>
          Referral code <span style={{ color: C.textFaint, fontWeight: 500 }}>· optional</span>
        </span>
        <input
          type="text"
          placeholder="Enter code"
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          style={{
            border: "none", background: "transparent",
            fontSize: 14, fontWeight: 600, color: C.text,
            outline: "none", fontFamily: FF,
            padding: 0, marginTop: 1,
            letterSpacing: "0.08em",
          }}
        />
      </div>
    </div>
  )
}

function PickCard({ onClick }) {
  return (
    <div
      className="rus-fade"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick() }}
      style={{
        position: "relative",
        padding: "20px",
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: 20, cursor: "pointer",
        fontFamily: FF,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.textMuted }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border }}
    >
      <div style={{
        position: "absolute", top: 14, right: 14,
        padding: "3px 8px", background: C.text, color: "#fff",
        borderRadius: 6, fontSize: 9, fontWeight: 800, letterSpacing: "0.06em",
      }}>
        RECOMMENDED
      </div>
      <div style={{
        width: 46, height: 46, borderRadius: 11,
        background: "#F4F4F5",
        border: `1px solid ${C.border}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: C.textSec, marginBottom: 16,
      }}>
        <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
          <path d="M5 2h7l4 4v11a1 1 0 01-1 1H5a1 1 0 01-1-1V3a1 1 0 011-1z"
            stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
          <path d="M12 2v4h4" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
          <path d="M7 10h6M7 13h6M7 7h2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>
        Upload your résumé
      </div>
      <div style={{ fontSize: 13, color: C.textSec, lineHeight: 1.5 }}>
        PDF format. We'll extract everything automatically.
      </div>
    </div>
  )
}

function UploadingCard({ fileName }) {
  const [secs, setSecs] = useState(30)
  useEffect(() => {
    const id = setInterval(() => setSecs((s) => (s > 0 ? s - 1 : 0)), 1000)
    return () => clearInterval(id)
  }, [])
  const almostDone = secs === 0
  const label = fileName ? `Analyzing ${fileName}…` : "Analyzing résumé…"
  return (
    <div className="rus-fade" style={{
      padding: 20, background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 20, fontFamily: FF,
    }}>
      <div style={{ width: 46, height: 46, position: "relative" }}>
        {almostDone ? (
          <svg width="46" height="46" viewBox="0 0 24 24" fill="none" style={{ animation: "rus-spin 0.9s linear infinite" }}>
            <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="2.5" />
            <path d="M12 2a10 10 0 0110 10" stroke={C.text} strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        ) : (
          <>
            <svg width="46" height="46" viewBox="0 0 46 46">
              <circle cx="23" cy="23" r="20" stroke={C.border} strokeWidth="2.5" fill="none" />
              <circle
                cx="23" cy="23" r="20" stroke={C.text} strokeWidth="2.5" fill="none"
                strokeDasharray={`${(secs / 30) * 125.6} 125.6`}
                strokeLinecap="round"
                transform="rotate(-90 23 23)"
                style={{ transition: "stroke-dasharray 1s linear" }}
              />
            </svg>
            <div style={{
              position: "absolute", inset: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 13, fontWeight: 700, color: C.text,
            }}>
              {secs}
            </div>
          </>
        )}
      </div>
      <div style={{
        marginTop: 16, fontSize: 16, fontWeight: 700, color: C.text,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {label}
      </div>
      <div style={{ marginTop: 4, fontSize: 13, color: C.textSec }}>
        {almostDone ? "Almost done…" : `It may take up to ${secs}s`}
      </div>
    </div>
  )
}

function DoneCard({ fileName }) {
  return (
    <div className="rus-fade" style={{
      padding: 20, background: "#F0F7F2",
      border: "1px solid #C2DCCA", borderRadius: 20, fontFamily: FF,
    }}>
      <div style={{
        width: 46, height: 46, borderRadius: "50%",
        background: "rgba(42,82,53,0.12)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <svg width="24" height="24" viewBox="0 0 20 20" fill="none">
          <path d="M4 10l5 5 7-7" stroke="#2A5235" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div style={{ marginTop: 16, fontSize: 16, fontWeight: 700, color: C.text }}>
        Résumé uploaded
      </div>
      {fileName && (
        <div style={{
          marginTop: 4, fontSize: 12, color: "#3A6845", fontWeight: 500,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {fileName}
        </div>
      )}
      <div style={{ marginTop: 4, fontSize: 13, color: C.textSec }}>
        Got it. Tap continue to move on.
      </div>
    </div>
  )
}

function ErrorCard({ message, onRetry }) {
  return (
    <div className="rus-fade" style={{
      padding: 20, background: "#FFF4F4",
      border: "1px solid #FECACA", borderRadius: 20, fontFamily: FF,
    }}>
      <div style={{
        width: 46, height: 46, borderRadius: "50%",
        background: "rgba(255,68,68,0.12)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <svg width="24" height="24" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="8" stroke="#CC2222" strokeWidth="1.6" />
          <path d="M10 6v4M10 14h.01" stroke="#CC2222" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </div>
      <div style={{ marginTop: 16, fontSize: 16, fontWeight: 700, color: C.text }}>
        Something went wrong
      </div>
      <div style={{ marginTop: 4, fontSize: 13, color: C.textSec, lineHeight: 1.5 }}>
        {message}
      </div>
      <button
        type="button"
        onClick={onRetry}
        style={{
          marginTop: 16, padding: "9px 16px",
          background: C.text, color: "#fff", border: "none",
          borderRadius: 10, cursor: "pointer",
          fontSize: 13, fontWeight: 600, fontFamily: FF,
        }}
      >
        Try again
      </button>
    </div>
  )
}
